import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  FocusEvent,
} from "react"

import { ContactAddress, Country } from "models/common"
import { get, set } from "lodash/fp"

import AutoCompleteInput from "components/inputs/AutoComplete"
import { Box } from "@material-ui/core"
import Button from "ui/buttons/Button"
import { ClientProfile } from "models/clientAgencyConf/profile"
import { ErrorsMap } from "api/http/utils"
import { Paginated } from "api/types"
import { TextInput } from "components/inputs/TextInput"
import { agencyClientStyles } from "./styles"
import { fetchCountries } from "api/geography"
import { throttle } from "lodash"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useQuery } from "react-query"

type AddressProps = {
  source?: ContactAddress
  address: "businessAddress" | "contact.address"
  disabled?: boolean
  updateSource: Dispatch<SetStateAction<Partial<ClientProfile> | undefined>>
  patchPart: (part: Partial<ClientProfile>) => void
  handleChange: (
    evt: ChangeEvent<
      | HTMLInputElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => void
  handleBlur: (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  errors?: ErrorsMap
  onCopyFrom?: () => void
  copyTitle?: string
}

const query_key = "countries"
function fetchOptions(_: string, name: string): Promise<Paginated<Country>> {
  const ps = new URLSearchParams([["limit", "200"]])
  if (name) {
    ps.set("name", name.trim())
  }
  return fetchCountries(query_key, ps.toString())
}

const fetchOptionsDebounced = throttle(fetchOptions, 100)

export function Address(props: AddressProps) {
  const { t } = useI18n()
  const {
    source,
    updateSource,
    patchPart,
    address,
    handleChange,
    handleBlur,
    disabled,
    errors,
    onCopyFrom,
    copyTitle,
  } = props
  const [countryTerm, setCountryTerm] = useState<string>("")
  const { hasAuthority } = useAuthenticated()

  const { data: countries, updatedAt } = useQuery(
    [query_key, countryTerm],
    fetchOptionsDebounced,
  )

  const countryOptions = [...(countries?.data?.map((c) => c.code) || []), ""]

  const getCountry = useCallback(
    (code: string) => {
      return countries?.data?.find((c) => c.code === code)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatedAt],
  )

  const handleCountryInputChange = useCallback((value: string) => {
    setCountryTerm(value)
  }, [])

  const handleCountryChange = useCallback(
    (countryCode: string | null) => {
      setCountryTerm("")
      updateSource((s) => set(`${address}.countryCode`, countryCode, s || {}))

      const part = set(`${address}.countryCode`, countryCode, {})
      patchPart(part)
    },
    [updateSource, address, patchPart],
  )

  return (
    <Box
      padding="0"
      display="grid"
      gridAutoFlow="row"
      gridAutoColumns="max-content"
      gridRowGap="var(--spacing-xl)"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <h5 className="BodyBody-1BlackRegular">{t(`agency.${address}`)}</h5>
        {onCopyFrom ? (
          <Button
            className={agencyClientStyles.textButton}
            variant="default"
            color="primary"
            onClick={onCopyFrom}
          >
            {copyTitle}
          </Button>
        ) : null}
      </Box>
      <TextInput
        name={`${address}.address1`}
        value={source?.address1 || ""}
        label={t("address.address1")}
        margin="none"
        size="medium"
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!get(`${address}.address1`, errors)}
        errorMessage={get(`${address}.address1`, errors)}
        disabled={
          !hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]) || disabled
        }
        className={agencyClientStyles.fieldWidthWider}
      />
      <TextInput
        id={`${address}.address2`}
        name={`${address}.address2`}
        value={source?.address2 || ""}
        label={t("address.address2")}
        margin="none"
        size="medium"
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!get(`${address}.address2`, errors)}
        errorMessage={get(`${address}.address2`, errors)}
        disabled={
          !hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]) || disabled
        }
        className={agencyClientStyles.fieldWidthWider}
      />
      <Box
        display="grid"
        gridAutoFlow="column"
        alignItems="center"
        gridColumnGap="var(--spacing-m)"
        gridAutoColumns="1fr"
      >
        <AutoCompleteInput<string>
          id={`agency.${address}.countryCode`}
          name={`${address}.countryCode`}
          label={t("address.country")}
          getOptionLabel={(code) => {
            const country = getCountry(code)
            return country?.name || ""
          }}
          value={source?.countryCode || ""}
          onChange={handleCountryChange}
          options={countryOptions}
          getOptionSelected={(opt, val) => opt === val}
          renderOption={(code) => {
            const country = getCountry(code)
            return country ? (
              <span>
                {country?.name} ({country?.code})
              </span>
            ) : (
              ""
            )
          }}
          blurOnChange={true}
          onInputChange={handleCountryInputChange}
          error={!!get(`${address}.countryCode`, errors)}
          helperText={get(`${address}.countryCode`, errors)}
          disabled={
            !hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]) || disabled
          }
        />
        <TextInput
          id={`${address}.state`}
          name={`${address}.state`}
          value={source?.state || ""}
          label={t("address.state")}
          margin="none"
          size="medium"
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!get(`${address}.state`, errors)}
          errorMessage={get(`${address}.state`, errors)}
          disabled={
            !hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]) || disabled
          }
        />
      </Box>
      <Box
        display="grid"
        gridAutoFlow="column"
        alignItems="center"
        gridColumnGap="var(--spacing-m)"
        gridAutoColumns="1fr"
      >
        <TextInput
          id={`${address}.city`}
          name={`${address}.city`}
          value={source?.city || ""}
          label={t("address.city")}
          margin="none"
          size="medium"
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!get(`${address}.city`, errors)}
          errorMessage={get(`${address}.city`, errors)}
          disabled={
            !hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]) || disabled
          }
        />
        <TextInput
          id={`${address}.zipCode`}
          name={`${address}.zipCode`}
          value={source?.zipCode || ""}
          label={t("address.zipCode")}
          margin="none"
          size="medium"
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!get(`${address}.zipCode`, errors)}
          errorMessage={get(`${address}.zipCode`, errors)}
          disabled={
            !hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]) || disabled
          }
          className={agencyClientStyles.zip}
        />
      </Box>
    </Box>
  )
}
